import { v2_ProductDetail } from '@crocodille/common';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../elements/button/button';
import Modal from '../../elements/modal/modal';
import { formatCurrency } from '@utils/format';
import Image from '../../elements/image/image';

import styles from './addons-modal.module.scss';
import SpinnerIcon from '@components/icons/spinner-icon';

type AddonOption = v2_ProductDetail['configuration'][number]['options'][number];

export interface AddonsModalProps {
  readonly open: boolean;
  readonly options: AddonOption[];
  readonly values: Partial<{ readonly [optionId: string]: number }>;
  readonly showPrices: boolean;
  onToggle(optionId: string, quantity: number): void;
  onConfirm(): void;
  onDecline(): void;
  readonly addingToCart?: boolean;
}

function hasNoSelectedAddons(values: AddonsModalProps['values']): boolean {
  return !Object.values(values).some((v) => (v ?? 0) > 0);
}

const Addons: React.FC<AddonsModalProps> = ({
  open,
  options,
  values,
  showPrices,
  onToggle,
  onConfirm,
  onDecline,
  addingToCart,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onDecline}
      closeOnOutsideClick={true}
      title={t('product.addons.title')}
      width="auto"
    >
      <div className={styles.scrollArea}>
        <div className={styles.grid}>
          {options.map((option) => {
            const selected = (values[option.itemCode] ?? 0) > 0;
            return (
              <div
                key={option.itemCode}
                className={cn(styles.item, { [styles.isSelected]: selected })}
                onClick={() => onToggle(option.itemCode, selected ? 0 : 1)}
              >
                <div className={styles.imgWrapper}>
                  <Image
                    src={option.image}
                    alt={option.name}
                    layout="fill"
                    objectFit="contain"
                  />
                </div>
                <span className={styles.name}>{option.name}</span>
                {showPrices && option.unitPrice != null ? (
                  <span className={styles.price}>
                    {formatCurrency(option.unitPrice, 'CZK', language)}
                  </span>
                ) : null}
                <div
                  className={cn(styles.check, {
                    [styles.isChecked]: selected,
                  })}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.buttons}>
        <Button
          size="large"
          symmetrical={false}
          variant="secondary"
          onClick={onDecline}
        >
          <span>{t('product.addons.decline')}</span>
          <Button.Icon>{addingToCart ? <SpinnerIcon /> : null}</Button.Icon>
        </Button>
        <Button
          size="large"
          symmetrical={false}
          onClick={onConfirm}
          disabled={hasNoSelectedAddons(values)}
        >
          <span>{t('product.addons.confirm')}</span>
        </Button>
      </div>
    </Modal>
  );
};

export default Addons;
